/**
 * Sector-aware cash-flow benchmark configuration.
 *
 * Thresholds are an initial heuristic screening policy, not peer-derived
 * facts and not investing recommendations. A later `source: 'peerPercentile'`
 * row can replace a heuristic metric profile without rewriting the UI.
 *
 * Sector taxonomy is shared; this file re-exports it so existing imports
 * keep working.
 */

import {
    BENCHMARK_SOURCE_HEURISTIC,
    HEURISTIC_EFFECTIVE_FROM,
} from './sectorTaxonomy';

export {
    ARCHETYPE_LABELS,
    BENCHMARK_SOURCE_HEURISTIC,
    CANONICAL_SECTORS,
    FALLBACK_SECTOR_NOTE,
    HEURISTIC_EFFECTIVE_FROM,
    HISTORICAL_WINDOW_BY_ARCHETYPE,
    METHODOLOGY_NOTE,
    NOT_APPLICABLE_ARCHETYPES,
    NOT_APPLICABLE_REASONS,
    SECTOR_ARCHETYPE_BY_ID,
    STATUS_LABELS,
} from './sectorTaxonomy';

export {
    VOLATILITY_PERCENTAGE_POINT_THRESHOLD,
    VOLATILITY_RELATIVE_THRESHOLD,
} from '../utils/benchmarkMath';

export const FCF_STABILITY_CAP_ARCHETYPES = new Set([
    'assetLightServices',
    'defensiveConsumer',
    'healthcareChemicals',
    'manufacturingIndustrial',
]);

export const SCORED_METRIC_IDS = [
    'fcfConversion',
    'grossCapexFcfConversion',
    'capexToCfo',
    'capexToDepreciation',
    'cfoToNetProfit',
    'fcfToNetProfit',
    'fcfMargin',
    'grossCapexFcfMargin',
    'workingCapitalToCfo',
    'dividendCoverage',
    'grossCapexFcfDividendCoverage',
    'cfoToEbit',
    'cfoToEbitda',
    'debtRepaymentToCfo',
    'dividendsToCfo',
    'capexToSales',
    'cfoToDebt',
    'cashFlowCoverage',
];

export const DIAGNOSTIC_ONLY_METRIC_IDS = new Set([
    'cfi',
    'cff',
    'netCashFlow',
    'investmentsPurchased',
    'investmentsSold',
    'proceedsFromBorrowings',
    'repaymentOfBorrowings',
    'netDebtCashFlow',
    'netInvestmentCashFlow',
]);

export const AUDIT_ONLY_METRIC_IDS = new Set(['reportedCfoOp']);

export const PERCENTAGE_METRIC_IDS = new Set([
    'fcfConversion',
    'grossCapexFcfConversion',
    'capexToCfo',
    'fcfMargin',
    'grossCapexFcfMargin',
    'workingCapitalToCfo',
    'capexToSales',
]);

export const FCF_CONVERSION_METRIC_IDS = new Set(['fcfConversion', 'grossCapexFcfConversion']);
export const DIVIDEND_COVERAGE_METRIC_IDS = new Set(['dividendCoverage', 'grossCapexFcfDividendCoverage']);
export const DIVIDEND_PAYOUT_METRIC_IDS = new Set(['dividendsToCfo']);
export const DEBT_COVERAGE_METRIC_IDS = new Set(['cfoToDebt', 'debtRepaymentToCfo']);
export const EARNINGS_CASH_CONVERSION_METRIC_IDS = new Set([
    'cfoToNetProfit',
    'fcfToNetProfit',
    'cfoToEbit',
    'cfoToEbitda',
]);
export const SELF_SUFFICIENCY_CAPEX_HEAVY_ARCHETYPES = new Set([
    'regulatedNetworkCapex',
    'longCycleProject',
    'commodityCyclical',
]);
export const SELF_SUFFICIENCY_SUSTAINED_FLAG_ARCHETYPES = new Set([
    'assetLightServices',
    'defensiveConsumer',
]);

export const WC_MOVEMENT_WARNING =
    'Large working-capital movement: inspect receivables, inventory, payables, and other working-capital drivers.';

export const WC_SIGN_NOTE =
    'Positive native cash-flow values support CFO and negative values absorb cash.';

export const NO_DIVIDEND_NOTE =
    'No dividend cash outflow in the evaluated period; coverage is not applicable.';

export const NO_DEBT_NOTE =
    'No interest-bearing debt in the evaluated period; this coverage measure is not applicable.';

export const SELF_SUFFICIENCY_RULE =
    'Self-funded when the magnitude of net CFI+CFF outflow does not exceed CFO. A net CFI+CFF inflow is treated as externally funded.';

export const CAPEX_DA_INVESTIGATION_NOTE =
    'A high Capex / Depreciation value can signal growth capex or heavy maintenance needs and requires comparison with returns on capital and capacity utilization.';

export const WC_LARGE_MOVEMENT_THRESHOLD = 30;

const HIGHER = 'higherIsBetter';
const LOWER = 'lowerIsBetter';
const BAND = 'band';

function heuristic(metricId, direction, thresholds) {
    return {
        source: BENCHMARK_SOURCE_HEURISTIC,
        effectiveFrom: HEURISTIC_EFFECTIVE_FROM,
        sampleSize: null,
        metricId,
        direction,
        thresholds,
        unit: PERCENTAGE_METRIC_IDS.has(metricId) ? 'percentage' : 'multiple',
    };
}

function higher(metricId, strong, watch) {
    return heuristic(metricId, HIGHER, { strong, watch });
}

function lower(metricId, strong, watch) {
    return heuristic(metricId, LOWER, { strong, watch });
}

function band(metricId, strongMin, strongMax, watchMin, watchMax) {
    return heuristic(metricId, BAND, { strongMin, strongMax, watchMin, watchMax });
}

function wcBand(strongMin, strongMax) {
    return band('workingCapitalToCfo', strongMin, strongMax, strongMin, strongMax);
}

function archetypeMetrics({
    fcfConversion,
    grossCapexFcfConversion,
    capexToCfo,
    capexToDepreciation,
    cfoToNetProfit,
    fcfToNetProfit,
    fcfMargin,
    workingCapitalToCfo,
    dividendCoverage,
    cfoToEbit,
    cfoToEbitda,
    debtRepaymentToCfo,
    dividendsToCfo,
    capexToSales,
    cfoToDebt,
    cashFlowCoverage,
}) {
    return {
        fcfConversion: higher('fcfConversion', fcfConversion[0], fcfConversion[1]),
        grossCapexFcfConversion: higher('grossCapexFcfConversion', grossCapexFcfConversion[0], grossCapexFcfConversion[1]),
        capexToCfo: lower('capexToCfo', capexToCfo[0], capexToCfo[1]),
        capexToDepreciation: band(
            'capexToDepreciation',
            capexToDepreciation[0],
            capexToDepreciation[1],
            capexToDepreciation[0],
            capexToDepreciation[3],
        ),
        cfoToNetProfit: higher('cfoToNetProfit', cfoToNetProfit[0], cfoToNetProfit[1]),
        fcfToNetProfit: higher('fcfToNetProfit', fcfToNetProfit[0], fcfToNetProfit[1]),
        fcfMargin: higher('fcfMargin', fcfMargin[0], fcfMargin[1]),
        grossCapexFcfMargin: higher('grossCapexFcfMargin', fcfMargin[0], fcfMargin[1]),
        workingCapitalToCfo: wcBand(workingCapitalToCfo[0], workingCapitalToCfo[1]),
        dividendCoverage: higher('dividendCoverage', dividendCoverage[0], dividendCoverage[1]),
        grossCapexFcfDividendCoverage: higher(
            'grossCapexFcfDividendCoverage',
            dividendCoverage[0],
            dividendCoverage[1],
        ),
        cfoToEbit: higher('cfoToEbit', cfoToEbit[0], cfoToEbit[1]),
        cfoToEbitda: higher('cfoToEbitda', cfoToEbitda[0], cfoToEbitda[1]),
        debtRepaymentToCfo: lower('debtRepaymentToCfo', debtRepaymentToCfo[0], debtRepaymentToCfo[1]),
        dividendsToCfo: lower('dividendsToCfo', dividendsToCfo[0], dividendsToCfo[1]),
        capexToSales: lower('capexToSales', capexToSales[0], capexToSales[1]),
        cfoToDebt: higher('cfoToDebt', cfoToDebt[0], cfoToDebt[1]),
        cashFlowCoverage: higher('cashFlowCoverage', cashFlowCoverage[0], cashFlowCoverage[1]),
    };
}

/**
 * Initial heuristic thresholds by economic archetype.
 * Percentage values are percentage points (80 means 80%, not 0.80).
 *
 * Future peer rows may use `{ source: 'peerPercentile', sampleSize, period, percentileRules }`.
 */
export const ARCHETYPE_THRESHOLDS = {
    assetLightServices: archetypeMetrics({
        fcfConversion: [80, 60],
        grossCapexFcfConversion: [75, 55],
        capexToCfo: [20, 35],
        capexToDepreciation: [0.7, 1.8, 1.9, 2.5],
        cfoToNetProfit: [1.0, 0.8],
        fcfToNetProfit: [0.9, 0.65],
        fcfMargin: [15, 8],
        workingCapitalToCfo: [-15, 20],
        dividendCoverage: [1.5, 1.0],
        cfoToEbit: [0.90, 0.70],
        cfoToEbitda: [0.75, 0.55],
        debtRepaymentToCfo: [0.15, 0.30],
        dividendsToCfo: [0.35, 0.50],
        capexToSales: [5, 10],
        cfoToDebt: [0.50, 0.30],
        cashFlowCoverage: [0.50, 0.30],
    }),
    defensiveConsumer: archetypeMetrics({
        fcfConversion: [70, 50],
        grossCapexFcfConversion: [65, 45],
        capexToCfo: [35, 50],
        capexToDepreciation: [0.8, 2.0, 2.01, 2.75],
        cfoToNetProfit: [0.95, 0.75],
        fcfToNetProfit: [0.75, 0.5],
        fcfMargin: [8, 4],
        workingCapitalToCfo: [-20, 20],
        dividendCoverage: [1.5, 1.0],
        cfoToEbit: [0.85, 0.65],
        cfoToEbitda: [0.70, 0.50],
        debtRepaymentToCfo: [0.20, 0.35],
        dividendsToCfo: [0.30, 0.45],
        capexToSales: [6, 12],
        cfoToDebt: [0.45, 0.25],
        cashFlowCoverage: [0.45, 0.28],
    }),
    healthcareChemicals: archetypeMetrics({
        fcfConversion: [65, 45],
        grossCapexFcfConversion: [60, 40],
        capexToCfo: [40, 55],
        capexToDepreciation: [0.8, 2.2, 2.21, 3.0],
        cfoToNetProfit: [0.9, 0.7],
        fcfToNetProfit: [0.65, 0.4],
        fcfMargin: [8, 3],
        workingCapitalToCfo: [-20, 20],
        dividendCoverage: [1.5, 1.0],
        cfoToEbit: [0.80, 0.60],
        cfoToEbitda: [0.65, 0.45],
        debtRepaymentToCfo: [0.20, 0.35],
        dividendsToCfo: [0.30, 0.45],
        capexToSales: [8, 14],
        cfoToDebt: [0.40, 0.22],
        cashFlowCoverage: [0.42, 0.26],
    }),
    manufacturingIndustrial: archetypeMetrics({
        fcfConversion: [55, 35],
        grossCapexFcfConversion: [50, 30],
        capexToCfo: [55, 70],
        capexToDepreciation: [0.8, 2.5, 2.51, 3.5],
        cfoToNetProfit: [0.85, 0.65],
        fcfToNetProfit: [0.5, 0.25],
        fcfMargin: [5, 1],
        workingCapitalToCfo: [-25, 20],
        dividendCoverage: [1.25, 1.0],
        cfoToEbit: [0.75, 0.55],
        cfoToEbitda: [0.60, 0.40],
        debtRepaymentToCfo: [0.25, 0.40],
        dividendsToCfo: [0.25, 0.40],
        capexToSales: [8, 14],
        cfoToDebt: [0.35, 0.20],
        cashFlowCoverage: [0.40, 0.25],
    }),
    commodityCyclical: archetypeMetrics({
        fcfConversion: [45, 25],
        grossCapexFcfConversion: [40, 20],
        capexToCfo: [70, 90],
        capexToDepreciation: [0.7, 3.0, 3.01, 4.0],
        cfoToNetProfit: [0.8, 0.6],
        fcfToNetProfit: [0.4, 0.15],
        fcfMargin: [4, 0],
        workingCapitalToCfo: [-30, 25],
        dividendCoverage: [1.25, 1.0],
        cfoToEbit: [0.70, 0.50],
        cfoToEbitda: [0.55, 0.35],
        debtRepaymentToCfo: [0.30, 0.50],
        dividendsToCfo: [0.25, 0.40],
        capexToSales: [12, 20],
        cfoToDebt: [0.25, 0.12],
        cashFlowCoverage: [0.30, 0.15],
    }),
    longCycleProject: archetypeMetrics({
        fcfConversion: [40, 20],
        grossCapexFcfConversion: [30, 10],
        capexToCfo: [80, 100],
        capexToDepreciation: [0.7, 3.0, 3.01, 4.0],
        cfoToNetProfit: [0.75, 0.5],
        fcfToNetProfit: [0.25, 0],
        fcfMargin: [3, 0],
        workingCapitalToCfo: [-40, 30],
        dividendCoverage: [1.25, 1.0],
        cfoToEbit: [0.65, 0.45],
        cfoToEbitda: [0.50, 0.30],
        debtRepaymentToCfo: [0.35, 0.55],
        dividendsToCfo: [0.20, 0.35],
        capexToSales: [15, 25],
        cfoToDebt: [0.20, 0.10],
        cashFlowCoverage: [0.25, 0.12],
    }),
    regulatedNetworkCapex: archetypeMetrics({
        fcfConversion: [35, 15],
        grossCapexFcfConversion: [25, 5],
        capexToCfo: [90, 110],
        capexToDepreciation: [0.8, 3.5, 3.51, 5.0],
        cfoToNetProfit: [0.75, 0.55],
        fcfToNetProfit: [0.2, 0],
        fcfMargin: [2, 0],
        workingCapitalToCfo: [-25, 20],
        dividendCoverage: [1.2, 1.0],
        cfoToEbit: [0.65, 0.45],
        cfoToEbitda: [0.50, 0.30],
        debtRepaymentToCfo: [0.35, 0.55],
        dividendsToCfo: [0.20, 0.35],
        capexToSales: [20, 30],
        cfoToDebt: [0.15, 0.08],
        cashFlowCoverage: [0.20, 0.10],
    }),
    media: archetypeMetrics({
        fcfConversion: [60, 40],
        grossCapexFcfConversion: [55, 35],
        capexToCfo: [40, 60],
        capexToDepreciation: [0.6, 2.5, 2.51, 3.5],
        cfoToNetProfit: [0.85, 0.65],
        fcfToNetProfit: [0.6, 0.35],
        fcfMargin: [6, 2],
        workingCapitalToCfo: [-25, 20],
        dividendCoverage: [1.25, 1.0],
        cfoToEbit: [0.75, 0.55],
        cfoToEbitda: [0.60, 0.40],
        debtRepaymentToCfo: [0.25, 0.40],
        dividendsToCfo: [0.30, 0.45],
        capexToSales: [8, 15],
        cfoToDebt: [0.35, 0.20],
        cashFlowCoverage: [0.40, 0.25],
    }),
    notApplicable: null,
};

export const DIAGNOSTIC_COPY = {
    cfi_investing_bridge: {
        default: 'This bridge shows where investing cash went. Absolute CFI is not scored as good or weak because it scales with company size and business model.',
        regulatedNetworkCapex: 'Network-capex industries may structurally have sustained negative CFI. Treat this as capital-allocation context, not a weakness by itself.',
        longCycleProject: 'Project businesses often show lumpy, negative CFI around construction cycles. Use the bridge to see capex versus other investing, not as a universal score.',
        commodityCyclical: 'Cyclical producers often invest heavily in up-cycles. Negative CFI here is context for capital allocation, not a traffic-light score.',
        assetLightServices: 'Asset-light businesses typically have modest CFI. Use the bridge to separate maintenance capex from financial investments.',
    },
    fcf_vs_cfi_context: {
        default: 'Compare internally generated FCF with total investing outflows. CFI is not an FCF deduction and is not scored as universally good or weak.',
        regulatedNetworkCapex: 'Regulated and network businesses can have FCF well below total CFI because of grid, spectrum, or capacity investment. This is diagnostic context only.',
    },
    capex_vs_cfi: {
        default: 'This chart shows how much of CFI is plant-and-equipment capex versus other investing. Native signs are kept; CFI is not scored.',
        regulatedNetworkCapex: 'A large share of CFI may be structural network capex. That pattern is expected in utilities and telecom, not automatically weak.',
    },
    investment_securities_vs_cfi: {
        default: 'Marketable-security purchases and sales can dominate CFI without changing operating capex. This is capital-allocation context, not a scored benchmark.',
    },
    debt_financing_bridge: {
        default: 'Debt raised versus debt repaid is a financing identity, not a sector quality score. Use it to see whether the company was a net borrower or net reducer.',
        financial: 'For banks and NBFCs, borrowing flows are core to the business model and should not be read as corporate leverage cash-flow quality.',
    },
    cfo_cfi_cff_overview: {
        default: 'CFO, CFI, and CFF together explain the change in cash. Absolute totals are not scored; they scale with size and should be read as a cash-flow map.',
        regulatedNetworkCapex: 'Utilities and telecom often pair positive CFO with persistently negative CFI and CFF shaped by capex and leverage. That pattern is diagnostic, not a red flag by itself.',
    },
    fcf_vs_debt_repayment: {
        default: 'This chart is useful for seeing whether free cash could cover debt repayment, but absolute repayment amounts are not given a sector traffic-light score.',
    },
};

export const SELF_SUFFICIENCY_COPY = {
    selfFunded: 'Operating cash covered the net investing and financing outflow in the evaluated window.',
    externallyFundedDefault: 'Investing and financing activity was not fully funded by CFO in the evaluated window.',
    capexHeavy: 'External funding in a single year is common for this capital-intensive archetype and is not automatically a warning sign.',
    sustainedFlag: 'Sustained external funding across multiple years is a more meaningful flag for this archetype than a single year.',
    financialInstitutions: 'CFI + CFF versus CFO is not a meaningful self-sufficiency check for banks, NBFCs, insurers, and similar financial institutions.',
};

/**
 * Which benchmark metrics each named Cash Flow comparison should evaluate.
 * `defaultMetricIds` are shown first (max two decision-relevant cards).
 */
// cfo_vs_operating_profit stays unmapped: cfoToEbit is scored on the Cash Flow Ratios table, but this comparison remains without a ChartModal benchmark by product decision.
export const COMPARISON_BENCHMARK_MAP = {
    cfo_vs_reported_fcf: {
        mode: 'scored',
        metricIds: ['fcfConversion', 'grossCapexFcfConversion', 'capexToCfo'],
        defaultMetricIds: ['fcfConversion', 'grossCapexFcfConversion'],
        includeStability: true,
        optionalMetricIds: ['grossCapexFcfConversion'],
    },
    cfo_vs_gross_capex_fcf: {
        mode: 'scored',
        metricIds: ['grossCapexFcfConversion', 'fcfConversion', 'capexToCfo'],
        defaultMetricIds: ['grossCapexFcfConversion', 'fcfConversion'],
        includeStability: true,
        optionalMetricIds: ['fcfConversion'],
    },
    cfo_vs_capex: {
        mode: 'scored',
        metricIds: ['capexToCfo', 'fcfConversion'],
        defaultMetricIds: ['capexToCfo', 'fcfConversion'],
        optionalMetricIds: ['fcfConversion'],
    },
    capex_vs_depreciation: {
        mode: 'scored',
        metricIds: ['capexToDepreciation'],
        defaultMetricIds: ['capexToDepreciation'],
        notes: [CAPEX_DA_INVESTIGATION_NOTE],
    },
    cfo_vs_net_profit: {
        mode: 'scored',
        metricIds: ['cfoToNetProfit'],
        defaultMetricIds: ['cfoToNetProfit'],
    },
    fcf_vs_net_profit: {
        mode: 'scored',
        metricIds: ['fcfToNetProfit'],
        defaultMetricIds: ['fcfToNetProfit'],
    },
    revenue_vs_fcf: {
        mode: 'scored',
        metricIds: ['fcfMargin'],
        defaultMetricIds: ['fcfMargin'],
    },
    revenue_vs_gross_capex_fcf: {
        mode: 'scored',
        metricIds: ['grossCapexFcfMargin'],
        defaultMetricIds: ['grossCapexFcfMargin'],
    },
    cfo_vs_working_capital: {
        mode: 'scored',
        metricIds: ['workingCapitalToCfo'],
        defaultMetricIds: ['workingCapitalToCfo'],
        notes: [WC_SIGN_NOTE],
    },
    working_capital_driver_bridge: {
        mode: 'scored',
        metricIds: ['workingCapitalToCfo'],
        defaultMetricIds: ['workingCapitalToCfo'],
        notes: [WC_SIGN_NOTE],
    },
    fcf_vs_dividends: {
        mode: 'scored',
        metricIds: ['dividendCoverage', 'grossCapexFcfDividendCoverage'],
        defaultMetricIds: ['dividendCoverage', 'grossCapexFcfDividendCoverage'],
        optionalMetricIds: ['grossCapexFcfDividendCoverage'],
    },
    gross_capex_fcf_vs_dividends: {
        mode: 'scored',
        metricIds: ['grossCapexFcfDividendCoverage', 'dividendCoverage'],
        defaultMetricIds: ['grossCapexFcfDividendCoverage', 'dividendCoverage'],
        optionalMetricIds: ['dividendCoverage'],
    },
    cfi_investing_bridge: { mode: 'diagnostic', diagnosticId: 'cfi_investing_bridge' },
    fcf_vs_cfi_context: { mode: 'diagnostic', diagnosticId: 'fcf_vs_cfi_context' },
    capex_vs_cfi: { mode: 'diagnostic', diagnosticId: 'capex_vs_cfi' },
    investment_securities_vs_cfi: { mode: 'diagnostic', diagnosticId: 'investment_securities_vs_cfi' },
    debt_financing_bridge: { mode: 'diagnostic', diagnosticId: 'debt_financing_bridge' },
    cfo_cfi_cff_overview: { mode: 'diagnostic', diagnosticId: 'cfo_cfi_cff_overview' },
    fcf_vs_debt_repayment: { mode: 'diagnostic', diagnosticId: 'fcf_vs_debt_repayment' },
};
